import { useMe } from './useMe'

/**
 * Per-company "billing handled externally" mode (Visma & co).
 *
 * Reads the staff-set backend flag
 * `CompanySettingsType.externalBillingEnabled`, already part of the `me`
 * query (`GetMeByProjectIds`, `src/api/auth-queries.ts`). Complements
 * `useInvoicingEnabled`: such a company keeps the whole invoicing BACKEND —
 * events and invoices are still generated for it and handed to the external
 * integration — but the internal invoicing MODULE is hidden on the frontend,
 * because their real invoices come out of their own finance system and the
 * module would only show a parallel set nobody sends.
 *
 * What it does, in one list:
 *   - hides the Invoicing module: sidebar entry, `/app/invoicing/...` routes
 *     (the platform "not enabled" notice instead), Settings → Invoicing card
 *     and route, the Legacy Billing Runs settings tab, the cockpit
 *     Recent-invoices / Overdue doors, omnisearch Invoice results, and the
 *     relocation wizard's billable-events link — the module-level answer is
 *     `useInvoicingModuleVisible`;
 *   - locks (greys, keeps readable) the invoice-related customer fields
 *     (billing frequency, invoice language/grouping/template, VAT toggles,
 *     e-invoice address) — their required rules drop with them, so an
 *     operator editing an individual is never blocked by a field they cannot
 *     fill in;
 *   - collapses the property Invoices tab to the legacy (crm) invoice list
 *     the integration exports from, labelled plainly "Invoices".
 *
 * What it KEEPS: the property Invoices tab with "Generate invoices for this
 * property", the sync frequency and Sync-to-Visma actions, the contact
 * header's invoicing customer number, the Visma export logs / Run export, and
 * Background jobs — the surfaces an external-billing office uses every day.
 *
 * Fails closed-as-OFF: an absent/unresolved flag means "normal invoicing",
 * never "hidden" — the opposite of a permission gate, since guessing wrong in
 * that direction would take a paying tenant's own billing away from them. The
 * backend refuses the module's writes independently (`invoicing/access.py`),
 * so this hook is the honest UI, not the wall.
 */
export const useExternalBillingEnabled = (): boolean =>
  Boolean(useMe()?.company?.companySettings?.externalBillingEnabled)
